(ns manage.poi.selectors.page
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium useGridApiRef]]
            ["src/svg/shapes/rectangle/rectangle-split-3x1.svg$default" :as ColumnsIcon]
            [cljs-bean.core :refer [->clj]]
            [common.form :as form]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.poi :as poi.q]
            [common.widgets.overlay-page :as overlay-page]
            [common.widgets.page :as page]
            [manage.poi.locations.columns :refer [location-columns get-row-id]]
            [manage.poi.selectors.form :refer [build-selector-payload empty-selector-defaults
                                               selector-default-values selector-form-fields]]
            [manage.poi.shared :refer [cursor-pagination use-cursor-pagination]]
            [reitit.frontend.easy :as r.easy]
            [uix.core :refer [$ defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(defui selector-matched-locations [{:keys [advertiser-id all-labels any-labels not-all-labels not-any-labels]}]
  (let [api-ref (useGridApiRef)
        {dataset-ready :isSuccess} (use-query (poi.q/ensure-dataset {:advertiser-id advertiser-id}))
        {:keys [cursor reset page-props]} (use-cursor-pagination)

        _ (uix/use-effect
           (fn [] (reset))
           [all-labels any-labels not-all-labels not-any-labels reset])

        {loc-data :data loc-pending :isPending}
        (use-query (poi.q/locations-query {:advertiser-id advertiser-id
                                           :all-labels all-labels
                                           :any-labels any-labels
                                           :not-all-labels not-all-labels
                                           :not-any-labels not-any-labels
                                           :page-after (:page-after cursor)
                                           :page-before (:page-before cursor)
                                           :enabled? dataset-ready}))

        {:keys [items pagination-props]} (page-props loc-data)]
    ($ :<>
       ($ Stack {:direction "row" :justifyContent "space-between" :alignItems "center" :sx #js {:mb 1}}
          ($ Typography {:variant "h6"} "Matched Locations")
          ($ Button {:endIcon ($ ColumnsIcon)
                     :onClick #(.. api-ref -current (showPreferences "columns"))}
             "Columns"))
       ($ :div {:style {:flex 1 :minHeight 0 :overflow "auto"}}
          ($ DataGridPremium
             {:apiRef api-ref
              :columns (location-columns nil)
              :loading loc-pending
              :rows (to-array (or items []))
              :getRowId get-row-id
              :disableRowSelectionOnClick true
              :disableColumnSorting true
              :disableColumnFilter true
              :hideFooter true
              :localeText #js {:noRowsLabel "No matching locations"}
              :initialState #js {:columns #js {:columnVisibilityModel #js {:monday false :tuesday false :wednesday false
                                                                           :thursday false :friday false :saturday false
                                                                           :sunday false
                                                                           :lat false :lon false
                                                                           :created-at false
                                                                           :postcode false
                                                                           :city false :address false
                                                                           :schedule false
                                                                           :actions false}}}}))
       ($ cursor-pagination pagination-props))))

(defui selector-edit-pane [{:keys [control handleSubmit label-options on-save isPending is-new?]}]
  (let [on-submit (fn [^js data]
                    (on-save (build-selector-payload data)))]
    ($ :form {:onSubmit (handleSubmit on-submit)}
       ($ Paper {:sx #js {:padding 3}}
          (selector-form-fields {:control control :label-options label-options :auto-focus? is-new?})
          ($ Stack {:direction "row" :justifyContent "flex-end" :sx #js {:mt 2}}
             ($ Button {:type "submit" :variant "contained"
                        :disabled isPending}
                (if is-new? "Create Selector" "Save")))))))

(defn- use-selector-page []
  (let [{:keys [advertiser-id] :as url-context} (use-subscribe [:url-context])
        {:keys [selector-id]} (use-subscribe [:router/query-parameters])
        is-new? (= selector-id "new")
        {selector :data isSuccess :isSuccess} (use-query (poi.q/selector-query
                                                          {:advertiser-id advertiser-id
                                                           :selector-id (when-not is-new? selector-id)}))
        {dataset-ready :isSuccess} (use-query (poi.q/ensure-dataset {:advertiser-id advertiser-id}))
        {available-labels :data} (use-query (poi.q/label-options-query {:advertiser-id advertiser-id
                                                                        :enabled? dataset-ready}))
        {update-selector :mutate update-pending :isPending} (use-mutation (poi.q/update-selector-mutation {:advertiser-id advertiser-id}))
        {create-selector :mutate create-pending :isPending} (use-mutation (poi.q/create-selector-mutation {:advertiser-id advertiser-id}))
        {:keys [control handleSubmit watch reset]} (form/use-form #js {:defaultValues (empty-selector-defaults)})
        _ (uix/use-effect (fn []
                            (when (and (not is-new?) isSuccess selector)
                              (reset (selector-default-values selector))))
                          [reset selector is-new? isSuccess (:id selector)])
        watched (->clj (watch))
        label-options (mapv (fn [t] {:value t :label t}) (or available-labels []))
        on-save (if is-new?
                  (fn [payload]
                    (create-selector payload
                                     #js {:onSuccess (fn [^js data]
                                                       (let [created (->clj data)]
                                                         (r.easy/replace-state :advertiser/poi-selector
                                                                               url-context
                                                                               {:selector-id (:id created)})))}))
                  (fn [payload]
                    (update-selector #js {:selector-id (:id selector)
                                          :payload payload}
                                     #js {:onSuccess (fn [_])})))]
    {:advertiser-id advertiser-id
     :is-new?       is-new?
     :ready?        (or is-new? isSuccess)
     :selector-name (when isSuccess (:name selector))
     :control       control
     :handleSubmit  handleSubmit
     :label-options label-options
     :on-save       on-save
     :isPending     (or update-pending create-pending)
     :watched       watched}))

(defui selector-page-content [{:keys [ref style]}]
  (let [{:keys [advertiser-id is-new? ready? selector-name
                control handleSubmit label-options on-save isPending
                watched]} (use-selector-page)]
    ($ overlay-page/overlay-page
       {:ref ref
        :style style}
       ($ Stack {:sx #js {:overflow "hidden" :flex 1}}
          ($ overlay-page/header
             {:title "POI Selector"
              :on-close #(.back js/history)
              :entity-name (if is-new? "New" selector-name)})
          (when ready?
            ($ page/content-padding
               {:direction "row"
                :gap 3
                :sx #js {:overflowY "auto"
                         :pt 3
                         :flex 1}}
               ($ Stack
                  {:sx #js {:width "300px" :flexShrink 0}}
                  ($ selector-edit-pane {:control control
                                         :handleSubmit handleSubmit
                                         :label-options label-options
                                         :on-save on-save
                                         :isPending isPending
                                         :is-new? is-new?}))
               (when-not is-new?
                 ($ Paper
                    {:sx #js {:flex 1
                              :padding 3
                              :display "flex"
                              :flexDirection "column"
                              :overflow "hidden"}}
                    ($ selector-matched-locations {:advertiser-id advertiser-id
                                                   :all-labels (vec (or (:all-labels watched) []))
                                                   :any-labels (vec (or (:any-labels watched) []))
                                                   :not-all-labels (vec (or (:not-all-labels watched) []))
                                                   :not-any-labels (vec (or (:not-any-labels watched) []))})))))))))

(def poi-selector-page (uix/as-react selector-page-content))
